import * as React from 'react'
import { Collapse, Alert } from 'antd'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { BLAZE_COMPANY } from '../../../../constants/general'
import { HelpLinks } from '../../../../constants/ExternalLinks'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
}

interface IProvinceComplianceInfo {
  province: string
  description: string
}

const provinceComplianceInfo: IProvinceComplianceInfo[] = [
  {
    province: 'BC',
    description: `In late-2019, the BC LDB removed their automated file uploading feature. Stores are now required to go to the BC cannabis compliance portal (ask your regulator to gain access) and input their category $ and weight values manually using ${BLAZE_COMPANY}'s report.\n\nMust submit by the 10th of every month.`,
  },
  {
    province: 'AB',
    description: `The AGLC provides a web portal to upload your final .xlsx file (ask your regulator to gain access). Their portal runs a series of checks on your numbers to ensure that they match up with your previous report, so please take that into account when making manual file modifications.\n\nMust submit by the 10th of every month.`,
  },
  {
    province: 'SK',
    description: `The SLGA asks stores to submit your final .csv reports via the online portal (ask your regulator to gain access).\n\nMust submit by the 10th of every month.`,
  },
  {
    province: 'ON',
    description: `The AGCO provides a portal to submit both the federal compliance report and the OCS sales report (ask your regulator to gain access). Please remember to download both files from ${BLAZE_COMPANY} before submitting.\n\nMust submit by the 5th of every month.`,
  },
  {
    province: 'MB',
    description: `The LGCA asks stores to submit their final reports through their web portal. MBLL requires report to be emailed directly.\n\nMust submit by the 7th of every month.`,
  },
  {
    province: 'NT',
    description: `The NTLCC asks stores to submit their final reports via email.\n\nMust submit by the 10th of every month.`,
  },
  {
    province: 'NU',
    description: `The NULC asks stores to submit their final reports via email.\n\nMust submit by the 7th of every month.`,
  },
  {
    province: 'NB',
    description: `The NB asks stores to submit their final reports via email.\n\nMust submit by the 10th of every month.`,
  },
  {
    province: 'Other',
    description: `Please contact ${BLAZE_COMPANY} to develop automated compliance reporting for your province.`,
  },
]

@inject('currentCompanyStore')
@observer
export class ComplianceInfo extends React.Component<Props, {}> {
  render() {
    return (
      <>
        <Collapse>
          <Collapse.Panel key='how-works' header='How do compliance reports work?'>
            <ol>
              <li>All inventory changes in {BLAZE_COMPANY} are recorded as "inventory logs".</li>
              <li>
                Each inventory log has a specific Health Canada reason code attached (sale,
                purchased, lost, destroyed, other, etc.).
              </li>
              <ul>
                <li>
                  <span className='text-success'>+</span> Purchased
                </li>
                <li>
                  <span className='text-success'>+</span> Transferred
                </li>
                <li>
                  <span className='text-success'>+</span> Refunds
                </li>
                <li>
                  <span className='text-success'>+</span> Other
                </li>
                <li>
                  <span className='text-danger'>-</span> Sold (in-store, online, wholesale)
                </li>
                <li>
                  <span className='text-danger'>-</span> Transferred
                </li>
                <li>
                  <span className='text-danger'>-</span> Returned
                </li>
                <li>
                  <span className='text-danger'>-</span> Destroyed
                </li>
                <li>
                  <span className='text-danger'>-</span> Lost
                </li>
                <li>
                  <span className='text-danger'>-</span> Other
                </li>
              </ul>
              <li>Reason codes can be changed in {BLAZE_COMPANY}.</li>
              <li>{BLAZE_COMPANY} analyzes the logs to build your final report.</li>
            </ol>
            <p>
              Note: Only products with a compliance category attached will be shown on your final
              compliance reports.
            </p>
            <p className='mb-0'>
              For more information, please refer to{' '}
              <a href={HelpLinks.REPORTS_COMPLIANCE}>this article</a>.
            </p>
          </Collapse.Panel>
          <Collapse.Panel key='how-to' header='How do I submit my compliance report?'>
            <table className='table'>
              <thead>
                <tr>
                  <th>Province</th>
                  <th>How to</th>
                </tr>
              </thead>
              <tbody>
                {provinceComplianceInfo.map((complianceInfo: IProvinceComplianceInfo) => (
                  <tr key={complianceInfo.province} style={{ whiteSpace: 'pre-wrap' }}>
                    <td>{complianceInfo.province}</td>
                    <td>{complianceInfo.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Collapse.Panel>
          <Collapse.Panel key='how-to-modify' header='How do I correct/modify my inventory logs?'>
            <p>
              Please refer to <a href={HelpLinks.REPORTS_COMPLIANCE}>this article</a> for a
              breakdown of common scenarios.
            </p>
            <Alert
              showIcon
              type='warning'
              message='Inventory log notes'
              description={`${BLAZE_COMPANY} highly recommends leaving notes for all inventory audits. This will make it easier for you and your staff to assign the correct reasons.`}
            />
          </Collapse.Panel>
          <Collapse.Panel
            key='previous-change'
            header='Common issue: Regenerated historical report values have changed'
          >
            <p>
              {BLAZE_COMPANY} uses your latest product and category information to generate
              historical compliance reports. We always recommend storing your final reports on your
              computer for future reference.
            </p>
            <p>Here is a list of actions that can affect your historical compliance values:</p>
            <ul>
              <li>Deleting a product</li>
              <li>Changing a product's SKU</li>
              <li>Changing a product's category</li>
              <li>Changing a product's cannabis weight</li>
              <li>Changing a product's cannabis volume</li>
              <li>Changing a product's net product weight</li>
              <li>Changing a product's retail price</li>
              <li>Changing a product's wholesale cost</li>
            </ul>
            <Alert
              showIcon
              type='warning'
              message='Limit unnecessary product deletion'
              description={`${BLAZE_COMPANY} highly recommends not deleting cannabis products once inventory has been added. Deleting products with inventory can cause discrepancies with opening and closing inventory values. Use the "Product edit history" feature to keep track of your product changes.`}
            />
          </Collapse.Panel>
          <Collapse.Panel key='negative-opening' header='Common issue: Negative inventory values'>
            <p>
              At a high level, provinces want to know: your opening inventory, all inventory changes
              (based on Health Canada reason codes), and your closing inventory.
            </p>
            <p>
              <b>If your opening inventory is negative: </b>Manually update your final report to set
              the opening quantity to 0, then add that number to your "other reductions" column. It
              is likely that your previous report has had a negative closing quantity - if so,
              please refer to the closing inventory steps below to re-submit your previous month's
              report.
            </p>
            <p>
              <b>If your closing inventory is negative: </b>Manually update your final report to set
              the closing quantity to 0, then add that number to your "other additions" column.
            </p>
            <p>
              In the future, this can be alleviated by auditing all negative inventory products{' '}
              <b>before</b> the end of the month.
            </p>
            <Alert
              showIcon
              type='warning'
              message='Historical data modification'
              description={`For security and accountability purposes, ${BLAZE_COMPANY} cannot modify historical inventory values. As a result, it will be up to your store to modify the xlsx/csv report manually before submission and explain to the regulator.`}
            />
          </Collapse.Panel>
          {this.props.currentCompanyStore!.company!.province === 'ON' && (
            <Collapse.Panel
              key='old-reports'
              header='How do I generate compliance reports prior to March 2021?'
            >
              <p>
                All reports, except the Inventory Adjustments(SRT#1), and Destructions(SRT#2)
                previously reported as periodic report, can be copied and pasted into the old AGCO
                template.
              </p>
              <p>
                To report Inventory Adjustments(previously referred to as SRT#1) prior to March
                2021, please remove the column “Date of Physical Inventory Count (yyyy/mm/dd)” and
                copy and paste the report directly into the old AGCO template.
              </p>
              <p>
                To report Destructions(previously referred to as SRT#2) prior to March 2021, please
                remove the column "Appeared in Prior Period as “TBD”?" and copy and paste the report
                directly into the old AGCO template.
              </p>
            </Collapse.Panel>
          )}
        </Collapse>
      </>
    )
  }
}
